import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <main className="min-h-screen bg-background-100/70 flex items-center justify-center pt-20">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 block mb-4"></i>
          <p className="text-foreground-600 mb-4">漫画未找到</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const categoryNames: Record<string, string> = {
    romance: '恋爱漫画',
    fantasy: '奇幻漫画',
    action: '动作漫画',
    thriller: '悬疑漫画',
    'slice-of-life': '日常漫画',
    comedy: '搞笑漫画',
    drama: '剧情漫画',
    completed: '完结漫画',
  };
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const chapterList = Array.from({ length: Math.min(comic.chapters, 12) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(2026, 7, 5 - i).toISOString().split('T')[0],
  }));

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updateDate,
    inLanguage: 'zh-CN',
    url: `https://hanmanonline.com/article/${comic.id}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
        { '@type': 'ListItem', position: 2, name: categoryNames[comic.category] || comic.category, item: `https://hanmanonline.com/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title },
      ],
    },
  };

  return (
    <main className="min-h-screen bg-background-100/70">
      <StructuredData data={articleStructuredData} />
      <article className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryNames[comic.category] || comic.category, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col md:flex-row gap-8 mt-4">
          <div className="w-full md:w-72 flex-shrink-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 flex items-center justify-center sticky top-24">
              <i className="ri-image-line text-5xl text-foreground-300"></i>
            </div>
          </div>

          <div className="flex-1">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {comic.tags.map((tag, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    comic.status === '已完结'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>作者：{comic.author}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                  {comic.rating}
                </span>
                <span>共 {comic.chapters} 话</span>
                {category && (
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                  >
                    {categoryNames[comic.category]}
                  </Link>
                )}
              </div>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200 p-5 mb-8">
              <h2 className="text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              <div className="flex items-center gap-3 mt-4 text-xs text-foreground-400">
                <span>最近更新：{comic.updateDate}</span>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200 p-5 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-foreground-900">章节列表</h2>
                <button className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                  正序/倒序
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                {chapterList.map((ch) => (
                  <button
                    key={ch.num}
                    className="flex items-center justify-between px-4 py-2.5 rounded-md border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 text-left cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{ch.title}</span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </button>
                ))}
              </div>
              {comic.chapters > 12 && (
                <button className="w-full mt-3 py-2.5 text-sm text-primary-500 hover:text-primary-600 font-medium border border-dashed border-background-300 rounded-md hover:border-primary-300 transition-all duration-200 cursor-pointer">
                  查看更多章节（共 {comic.chapters} 话）
                </button>
              )}
            </div>

            {relatedComics.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground-900 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/article/${rc.id}`}
                      className="group block bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                    >
                      <div className="aspect-[3/4] bg-background-100 flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-foreground-300"></i>
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                          {rc.title}
                        </p>
                        <p className="text-[11px] text-foreground-400 mt-0.5">
                          {rc.author} · {rc.rating}分
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}